import { useEffect, useState } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import { DefaultButton } from '../../../components/Button/Button';
import { SelectInput } from '../../../components/Input/Input';
import { alertError, alertSuccess } from '../../../components/Toast';
import { sizeIdToOption } from '../../../screens/ModalBudget/utils/constants';
import { BUDGET_TABS } from '../../../screens/ModalBudget/utils/enum';
import { supabase } from '../../../supabaseClient';
import './ImportBudgetModal.scss';

export const OSImportBudget = ({ budget, company, onImport, values }) => {
  const [budgets, setBudgets] = useState([]);
  const [userSelection, setUserSelection] = useState([]);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [budgetsData, setBudgetsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectKey, setSelectKey] = useState(0);

  const [localImportedBudgets, setLocalImportedBudgets] = useState([]);

  // Effect to load the master list of budgets
  useEffect(() => {
    loadBudgets();
  }, [
    company.id,
    values?.client,
    values?.vehicle,
    values?.ImportedBudgetOS,
    localImportedBudgets,
  ]);

  // Effect to derive the final selected budgets from the master list and user interaction
  useEffect(() => {
    const importedBudgets = budgets.filter((b) => b.isImported);
    const uniqueUserSelection = userSelection.filter(
      (userItem) =>
        !importedBudgets.some((impItem) => impItem.value === userItem.value)
    );
    setSelectedBudgets([...importedBudgets, ...uniqueUserSelection]);
  }, [budgets, userSelection]);

  // Effect to fetch details when the final selection changes
  useEffect(() => {
    if (selectedBudgets && selectedBudgets.length > 0) {
      loadBudgetsDetails();
    } else {
      setBudgetsData([]);
    }
  }, [selectedBudgets]);

  const loadBudgets = async () => {
    try {
      // First, get all imported budget IDs from the ImportedBudgetOS table
      const { data: globalImportedBudgets, error: importedError } = await supabase
        .from('ImportedBudgetOS')
        .select('budget_id');

      if (importedError) {
        console.error('Erro ao carregar orçamentos importados:', importedError);
      }

      const globalImportedBudgetIds = globalImportedBudgets ? globalImportedBudgets.map(b => b.budget_id) : [];

      let query = supabase
        .from('Budget')
        .select(
          `
              id,
              code,
              client_name,
              vehicle_name,
              created_at,
              status
            `
        )
        .eq('id_company', company.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (values && values.client) {
        query = query.eq(
          'client',
          (values.client && values.client.value) || values.client
        );
      }

      if (values && values.vehicle) {
        query = query.eq(
          'vehicle',
          (values.vehicle && values.vehicle.value) || values.vehicle
        );
      }

      const { data, error } = await query;

      if (!error) {
        const currentSessionImportedIds = [
          ...(values?.ImportedBudgetOS?.map((b) => b.budget_id) || []),
          ...localImportedBudgets.map((b) => b.id),
        ];

        const allBudgets = data.map((budget) => ({
          value: budget.id,
          label: `${budget.code} - ${budget.client_name} - ${budget.vehicle_name}`,
          ...budget,
          isImported: currentSessionImportedIds.includes(budget.id),
          isGloballyImported: globalImportedBudgetIds.includes(budget.id),
        }));
        setBudgets(allBudgets);
      } else {
        alertError('Erro ao carregar orçamentos');
      }
    } catch (error) {
      console.error('Erro ao carregar orçamentos:', error);
      alertError('Erro ao carregar orçamentos');
    }
  };

  const loadBudgetsDetails = async () => {
    if (!selectedBudgets || selectedBudgets.length === 0) return;

    try {
      setLoading(true);
      const budgetIds = selectedBudgets.map((budget) => budget.value);

      const { data, error } = await supabase
        .from('Budget')
        .select('*')
        .in('id', budgetIds);

      if (!error && data) {
        const dataWithFlags = data.map((budgetItem) => {
          const selected = selectedBudgets.find(
            (b) => b.value === budgetItem.id
          );
          return {
            ...budgetItem,
            isImported: !!(selected && selected.isImported),
          };
        });
        setBudgetsData(dataWithFlags);
      } else {
        alertError('Erro ao carregar detalhes dos orçamentos');
      }
    } catch (error) {
      alertError('Erro ao carregar detalhes dos orçamentos');
    } finally {
      setLoading(false);
    }
  };

  function onFinishImport() {}

  const handleDeleteImportedBudget = async (budgetId) => {
    try {
      if (values?.id) {
        const { error } = await supabase
          .from('ImportedBudgetOS')
          .delete()
          .eq('budget_id', budgetId)
          .eq('service_order_id', values.id);

        if (error) {
          console.error('Erro ao remover orçamento importado:', error);
          alertError('Erro ao remover orçamento importado');
          return;
        }
      } else {
        setLocalImportedBudgets((prev) =>
          prev.filter((b) => b.id !== budgetId)
        );
      }

      const updatedBudgets = budgets.map((b) =>
        b.value === budgetId ? { ...b, isImported: false } : b
      );
      setBudgets(updatedBudgets);

      alertSuccess('Orçamento removido da lista de importados');
    } catch (error) {
      console.error('Erro ao remover orçamento importado:', error);
      alertError('Erro ao remover orçamento importado');
    }
  };

  const handleImport = async () => {
    if (!budgetsData || budgetsData.length === 0) {
      alertError('Nenhum orçamento selecionado');
      return;
    }

    try {
      const importData = {
        bodyshopServices: [],
        aeServices: [],
        parts: [],
        details: {},
      };

      let idCounter = Date.now();

      // 🔑 Apenas processa orçamentos que NÃO foram importados ainda
      const newBudgetsToImport = budgetsData.filter((b) => !b.isImported);

      newBudgetsToImport.forEach((budgetData) => {
        idCounter = processServices(
          budgetData.service,
          'bodyshopServices',
          importData,
          budgetData.code,
          idCounter,
          budgetData.bodyshop_percentage_discount
        );
        idCounter = processServices(
          budgetData.ae_service,
          'aeServices',
          importData,
          budgetData.code,
          idCounter,
          budgetData.aesthetics_percentage_discount
        );
        idCounter = processServices(
          budgetData.parts,
          'parts',
          importData,
          budgetData.code,
          idCounter,
          budgetData.parts_percentage_discount
        );
      });

      // 🔑 Sempre pega os detalhes do primeiro selecionado (se for novo)
      if (!values?.id && selectedBudgets.length > 0) {
        const firstSelected = budgetsData.find(
          (b) => b.id === selectedBudgets[0].value
        );
        if (firstSelected && !firstSelected.isImported) {
          processDetails(firstSelected, importData.details);
        }
      }

      // 🔑 SEMPRE importa o tamanho do primeiro orçamento selecionado, mesmo para OS existente
      if (selectedBudgets.length > 0) {
        const firstSelected = budgetsData.find(
          (b) => b.id === selectedBudgets[0].value
        );
        if (firstSelected && !firstSelected.isImported && firstSelected.size) {
          // Converte id numérico (2/3/4) -> objeto { id, value, label }, formato
          // esperado em todo o modal da OS. Mantém a classificação do orçamento.
          importData.details.size = sizeIdToOption(firstSelected.size);
        }
      }

      importData.newlyImportedBudgets = newBudgetsToImport;

      onImport(importData);

      if (values?.id && newBudgetsToImport.length > 0) {
        const importedBudgetsToInsert = newBudgetsToImport.map((budget) => ({
          budget_id: budget.id,
          service_order_id: values.id,
        }));

        const { error: insertError } = await supabase
          .from('ImportedBudgetOS')
          .insert(importedBudgetsToInsert);

        if (insertError) {
          console.error('Erro ao salvar orçamentos importados:', insertError);
          alertError('Erro ao salvar referência dos orçamentos importados.');
          return;
        }
      } else {
        setLocalImportedBudgets((prev) => [
          ...prev,
          ...newBudgetsToImport.map((b) => ({ ...b, isImported: true })),
        ]);
      }

      alertSuccess(
        `Dados de ${newBudgetsToImport.length} orçamento(s) importados com sucesso!`
      );

      const newlyImportedIds = newBudgetsToImport.map((b) => b.id);
      const updatedBudgets = budgets.map((b) =>
        newlyImportedIds.includes(b.value) ? { ...b, isImported: true } : b
      );
      setBudgets(updatedBudgets);

      setUserSelection([]);
      setSelectedBudgets([]);
      setSelectKey((prev) => prev + 1);
      onFinishImport();
    } catch (error) {
      alertError('Erro ao importar dados dos orçamentos');
    }
  };

  const processServices = (
    serviceData,
    targetKey,
    importData,
    budgetCode,
    idCounter,
    discountPercentage
  ) => {
    if (!serviceData) return idCounter;

    try {
      const services = JSON.parse(serviceData);
      const servicesWithId = services.map((service, index) => {
        // Create a deep copy to avoid reference issues
        let processedService = {
          ...JSON.parse(JSON.stringify(service)),
          id: idCounter + index,
          originalBudgetCode: budgetCode,
        };

        // Congela preço E tempo do serviço importado (editedPrice + editedHour),
        // senão o recálculo por tamanho sobrescreve o tempo com o da tabela mestre
        // e o valor total fica diferente do orçamento de origem.
        if (targetKey === 'bodyshopServices') {
          processedService.editedPrice = true;
          processedService.editedHour = true;
        }

        // Apply discount to service values if discount percentage exists
        if (discountPercentage && discountPercentage > 0) {
          const discountMultiplier = (100 - discountPercentage) / 100;

          // Apply discount to unitaryValue for all service types
          if (processedService.unitaryValue) {
            let numericValue;
            if (typeof processedService.unitaryValue === 'string') {
              numericValue = parseFloat(
                processedService.unitaryValue.replace(',', '.')
              );
            } else {
              numericValue = parseFloat(processedService.unitaryValue);
            }

            if (!isNaN(numericValue)) {
              const discountedValue = numericValue * discountMultiplier;
              processedService.unitaryValue = discountedValue
                .toFixed(2)
                .replace('.', ',');
            }
          }

          // For non-bodyshop services, also apply discount to other value fields
          if (targetKey !== 'bodyshopServices') {
            // Apply discount to 'value' field (for parts - numeric, or ae_services - string)
            if (processedService.value) {
              if (typeof processedService.value === 'number') {
                processedService.value =
                  processedService.value * discountMultiplier;
              } else if (typeof processedService.value === 'string') {
                // Handle string values like "500.000"
                const numericValue = parseFloat(processedService.value);
                if (!isNaN(numericValue)) {
                  processedService.value = (
                    numericValue * discountMultiplier
                  ).toFixed(3);
                }
              }
            }

            // Apply discount to p_value field (present in ae_services)
            if (processedService.p_value) {
              let numericValue;
              if (typeof processedService.p_value === 'string') {
                numericValue = parseFloat(
                  processedService.p_value.replace(',', '.')
                );
              } else {
                numericValue = parseFloat(processedService.p_value);
              }

              if (!isNaN(numericValue)) {
                const discountedValue = numericValue * discountMultiplier;
                // Keep the same format as original
                if (
                  typeof processedService.p_value === 'string' &&
                  processedService.p_value.includes(',')
                ) {
                  processedService.p_value = discountedValue
                    .toFixed(2)
                    .replace('.', ',');
                } else {
                  processedService.p_value = discountedValue.toString();
                }
              }
            }
          }

          // Remove total so it gets recalculated with the new values
          if (processedService.total) {
            delete processedService.total;
          }
        }

        return processedService;
      });

      importData[targetKey].push(...servicesWithId);
      return idCounter + services.length;
    } catch (error) {
      console.error(`Erro ao processar ${targetKey}:`, error);
      return idCounter;
    }
  };

  const processDetails = (budgetData, details) => {
    const detailFields = [
      'client',
      'client_name',
      'vehicle',
      'payment',
      'id_consultant',
      'price', // valor/hora: taxa (hora linear) ou id da tabela (hora tabela)
      'hour', // flag hora linear (false/null) x hora tabela (true)
      // 'size' definido separadamente (convertido id->objeto) em handleImport
      // 'aesthetics_discount',
      // 'aesthetics_percentage_discount',
      // 'bodyshop_discount',
      // 'bodyshop_percentage_discount',
      // 'parts_discount',
      // 'parts_percentage_discount',
    ];

    detailFields.forEach((field) => {
      const value = budgetData[field];
      // 🔑 Só seta se ainda não existe no objeto
      if (
        details[field] === undefined &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== 0
      ) {
        details[field] = value;
      }
    });
  };

  const getConsolidatedSummary = (budgets) => {
    const defaultSummary = {
      totalBodyshopCount: 0,
      totalAeServicesCount: 0,
      totalPartsCount: 0,
      detailsAvailable: false,
      budgetsCount: 0,
    };

    if (!budgets || budgets.length === 0) return defaultSummary;

    let totalBodyshopCount = 0;
    let totalAeServicesCount = 0;
    let totalPartsCount = 0;
    let detailsAvailable = false;

    budgets.forEach((budgetData) => {
      try {
        if (budgetData.service) {
          totalBodyshopCount += JSON.parse(budgetData.service).length;
        }
        if (budgetData.ae_service) {
          totalAeServicesCount += JSON.parse(budgetData.ae_service).length;
        }
        if (budgetData.parts) {
          totalPartsCount += JSON.parse(budgetData.parts).length;
        }

        if (!detailsAvailable) {
          const detailFields = [
            'payment',
            'id_consultant',
            'expiration',
            'deadline',
            'status',
            'observation',
            'annotation',
          ];

          detailsAvailable = detailFields.some((field) => {
            const value = budgetData[field];
            return (
              value !== null &&
              value !== undefined &&
              value !== '' &&
              value !== 0
            );
          });
        }
      } catch (error) {
        console.error('Erro ao processar dados do orçamento:', error);
      }
    });

    return {
      totalBodyshopCount,
      totalAeServicesCount,
      totalPartsCount,
      detailsAvailable,
      budgetsCount: budgets.length,
    };
  };

  const getBudgetBreakdown = () => {
    if (!budgetsData || budgetsData.length === 0) return [];

    return budgetsData.map((budgetData) => {
      let bodyshopCount = 0;
      let aeServicesCount = 0;
      let partsCount = 0;

      try {
        if (budgetData.service) {
          bodyshopCount = JSON.parse(budgetData.service).length;
        }
        if (budgetData.ae_service) {
          aeServicesCount = JSON.parse(budgetData.ae_service).length;
        }
        if (budgetData.parts) {
          partsCount = JSON.parse(budgetData.parts).length;
        }
      } catch (error) {
        console.error('Erro ao processar dados do orçamento:', error);
      }

      return {
        code: budgetData.code,
        client_name: budgetData.client_name,
        vehicle_name: budgetData.vehicle_name,
        bodyshopCount,
        aeServicesCount,
        partsCount,
        total: bodyshopCount + aeServicesCount + partsCount,
        isImported: budgetData.isImported,
      };
    });
  };

  const breakdown = getBudgetBreakdown();
  const importedBreakdown = breakdown.filter((b) => b.isImported);
  const newSelections = budgetsData.filter((b) => !b.isImported);
  const hasNewSelections = newSelections.length > 0;
  const newSummary = getConsolidatedSummary(newSelections);

  return (
    <div
      style={{
        display: budget !== BUDGET_TABS.IMPORT_BUDGET ? 'none' : 'block',
      }}
      className="import-budget-container"
    >
      <div className="import-budget-modal-header">
        <h3>Importar dados de Orçamento</h3>
      </div>

      <div className="import-budget-modal-content">
        <div className="select-budget-section void-class">
          <SelectInput
            key={selectKey}
            isMulti={true}
            label="Selecione os Orçamentos"
            placeholder="Escolha os orçamentos para importar"
            options={budgets.filter((b) => !b.isImported && !b.isGloballyImported)}
            value={userSelection}
            onChange={setUserSelection}
            isSearchable
          />
        </div>

        {loading && (
          <div className="loading-section">
            <p>Carregando detalhes dos orçamentos...</p>
          </div>
        )}

        {!loading && budgetsData.length > 0 && (
          <div className="budget-summary">
            {hasNewSelections && (
              <>
                <h4>
                  Dados que serão importados de {newSelections.length}{' '}
                  orçamento(s):
                </h4>

                <div className="summary-grid">
                  {newSummary.totalBodyshopCount > 0 && (
                    <div className="summary-item">
                      <span className="count">
                        {newSummary.totalBodyshopCount}
                      </span>
                      <span className="label">
                        Serviços de Funilaria e Pintura
                      </span>
                    </div>
                  )}
                  {newSummary.totalAeServicesCount > 0 && (
                    <div className="summary-item">
                      <span className="count">
                        {newSummary.totalAeServicesCount}
                      </span>
                      <span className="label">Serviços Estéticos</span>
                    </div>
                  )}
                  {newSummary.totalPartsCount > 0 && (
                    <div className="summary-item">
                      <span className="count">
                        {newSummary.totalPartsCount}
                      </span>
                      <span className="label">Peças</span>
                    </div>
                  )}
                </div>
                {newSummary.totalBodyshopCount === 0 &&
                  newSummary.totalAeServicesCount === 0 &&
                  newSummary.totalPartsCount === 0 &&
                  !newSummary.detailsAvailable && (
                    <p className="no-data">
                      Os orçamentos selecionados não possuem dados para
                      importar.
                    </p>
                  )}
              </>
            )}

            {importedBreakdown.length >= 1 && (
              <div className="budget-breakdown">
                <h5>Orçamentos importados:</h5>
                <div className="breakdown-list">
                  {importedBreakdown.map((budget, index) => (
                    <div key={index} className="breakdown-item">
                      <div className="budget-info">
                        <strong>{budget.code}</strong> - {budget.client_name} -{' '}
                        {budget.vehicle_name}
                      </div>
                      <div className="budget-counts">
                        {budget.bodyshopCount > 0 && (
                          <span>{budget.bodyshopCount} funilaria</span>
                        )}
                        {budget.aeServicesCount > 0 && (
                          <span>{budget.aeServicesCount} estéticos</span>
                        )}
                        {budget.partsCount > 0 && (
                          <span>{budget.partsCount} peças</span>
                        )}
                        {budget.total === 0 && (
                          <span className="no-items">sem itens</span>
                        )}
                      </div>
                      {/* <button
                        className="delete-imported-budget"
                        onClick={() => {
                          const budgetData = budgetsData.find(b => b.code === budget.code);
                          if (budgetData) {
                            handleDeleteImportedBudget(budgetData.id);
                          }
                        }}
                        title="Remover orçamento importado"
                      >
                        <AiOutlineClose />
                      </button> */}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="import-budget-modal-footer">
        <DefaultButton
          label="Importar"
          icon={<AiOutlineCheck />}
          onClick={handleImport}
          disabled={
            !hasNewSelections ||
            loading ||
            (newSummary &&
              newSummary.totalBodyshopCount === 0 &&
              newSummary.totalAeServicesCount === 0 &&
              newSummary.totalPartsCount === 0 &&
              !newSummary.detailsAvailable)
          }
          className="import-button"
        />
      </div>
    </div>
  );
};
